/* AUTOGENERATED: DO NOT EDIT */

/**
 * A code is a string that represents a concept in a code system.
 *
 * This is a "NewType" (see https://kubyshkin.name/posts/newtype-in-typescript/).
 */
export type Code = string & { readonly __tag: unique symbol };

/** Creates a Code from a string. */
export const code = (value: string) => value as Code;

/**
 * A CodeableConcept represents a value that is usually supplied by providing a reference to
 * one or more terminologies or ontologies but may also be defined by the provision of text.
 *
 * References:
 *     `CodeableConcept data type <https://www.hl7.org/fhir/datatypes.html#CodeableConcept>`_.
 */
export interface CodeableConcept {
  coding: Array<Coding>;
  text?: string | null;
}

/**
 * A Coding is a representation of a defined concept using a symbol from a defined "code system".
 *
 * References:
 *     `Coding data type <https://www.hl7.org/fhir/datatypes.html#Coding>`_.
 */
export interface Coding {
  code: string;
  system: string;
  domain?: string | null;
  display?: string | null;
}

export type Domain =
  | "Condition"
  | "Drug"
  | "Regimen"
  | "Procedure"
  | "Measurement"
  | "Condition era"
  | "Drug era"
  | "Observation"
  | "Patient";

export interface ItemInfo {
  /** The name for the value set.  It should be short. */
  name: string;

  /**
   * The human-readable title for the value set.  It should describe the value
   * set in more detail.
   */
  title: string;

  /** An explication about this value set */
  description?: string | null;
}

export interface Mapping {
  info: ItemInfo;
  mapping: { [key: string]: Array<string | number> };
}

export interface OmopCoding {
  id: string;
  name: string;
  domain: string;
  class_name: string;
  vocabulary: string;
  code: string;
}

export interface OmopConcept {
  concept_id: number;
  concept_name: string;
  domain_id: string;
  concept_class_id: string;
  vocabulary_id?: string | null;
  concept_code?: string | null;
  valid_start_date?: number | null;
  valid_end_date?: number | null;
  invalid_reason?: string | null;
  standard_concept?: string | null;
}

export interface Period {
  id: number;
  start: string;
  end: string;
}

/**
 * A measured amount (or an amount that can potentially be measured).
 *
 * How to understand the value:
 * - "<": The actual value is less than the given value.
 * - "<=": The actual value is less than or equal to the given value.
 * - ">=": The actual value is greater than or equal to the given value.
 * - ">": The actual value is greater than the given value.
 *
 * References:
 *     `Quantity data type <https://www.hl7.org/fhir/datatypes.html#quantity>`_.
 */
export interface Quantity {
  value: number;
  unit?: Coding | null;
  comparator: "<" | "<=" | ">=" | ">" | "=" | null;
}

export interface Range {
  low: Quantity | null;
  high: Quantity | null;
}

export interface SimplifiedCoding {
  code: string;
  display: string;
}

export interface Synonym {
  language: number;
  display: string;
}

export interface ValueParameterSet {
  concept: OmopConcept;

  /** The default set of possible values for a code */
  default: Array<SimplifiedCoding>;

  /** Some conditions might be related to adverse events */
  adverse_events?: Array<SimplifiedCoding> | null;

  /** Some measurements might have a list of units */
  measurement_units?: Array<SimplifiedCoding> | null;
}

/**
 * A value set groups codings together.  You could have separate value sets for
 * drugs, diagnosis, ...
 */
export interface ValueSet {
  /** The name for the value set.  It should be short. */
  info: ItemInfo;
  codings: Array<SimplifiedCoding>;
}
